import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { resetModal } from '@guryou/html-components';
import { changeUserAvatar, getCurrentUserAccount, resetState, stateIsLoaded, stateIsLoading, UPLOAD_FILE, uploadFileToS3 } from '@guryou/redux';

const UserImageForm = ({ onClose }) => {
  const { t } = useTranslation();
  const [image, setImages] = useState();
  const [file, setFile] = useState();
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const upload = useSelector(state => state.files.upload);
  const { changeAvatar } = useSelector(state => state.user);

  const isLoading = stateIsLoading(changeAvatar) || stateIsLoading(upload);

  useEffect(() => {
    if (stateIsLoaded(upload)) {
      dispatch(resetState(UPLOAD_FILE));
      dispatch(getCurrentUserAccount(true));
      dispatch(resetModal());
      if (onClose) onClose();
    }
  }, [dispatch, upload, upload.version, onClose]);

  useEffect(() => {
    if (file && stateIsLoaded(changeAvatar)) {
      dispatch(uploadFileToS3(file, changeAvatar.data?.presignedUrl));
    }
  }, [changeAvatar, changeAvatar.version, dispatch, file]);

  const handleFileSelect = files => {
    if (files && files.length > 0) {
      const selectedFile = files[0];
      setImages([selectedFile]);
      setFile(selectedFile);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleFileInputChange = e => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (image && image.length > 0) {
      const fileType = image[0].name.split('.')[1];
      setFile(image[0]);
      dispatch(changeUserAvatar(fileType));
    }
  };

  const handleRemove = () => {
    setImages(null);
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className={`animate-spin rounded-full h-12 w-12 border-4 border-[var(--feel-primary)] border-t-transparent`}></div>
            <p className="text-gray-600">{t('GENERAL.uploading')}</p>
          </div>
        </div>
      ) : (
        <>
          {!preview ? (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging ? `border-[var(--feel-primary)] bg-[var(--feel-primary)]/5` : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="flex flex-col items-center gap-4">
                <svg
                  className={`w-12 h-12 ${isDragging ? `text-[var(--feel-primary)]` : 'text-gray-400'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    {t('GENERAL.dragAndDrop')}{' '}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className={`font-medium text-[var(--feel-primary)] hover:underline`}
                    >
                      {t('GENERAL.browse')}
                    </button>
                  </p>
                  <p className="text-xs text-gray-500">{t('GENERAL.supportedFormats')}</p>
                </div>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileInputChange} className="hidden" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative inline-block">
                <img src={preview} alt="Preview" className="w-48 h-48 rounded-lg object-cover border-2 border-gray-200" />
                <button
                  type="button"
                  onClick={handleRemove}
                  className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  aria-label={t('GENERAL.delete')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <button type="button" onClick={() => fileInputRef.current?.click()} className="text-sm text-gray-600 hover:text-gray-800 underline">
                {t('GENERAL.changeImage')}
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileInputChange} className="hidden" />
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
              {t('GENERAL.cancel')}
            </button>
            <button
              type="submit"
              disabled={!image || image.length === 0}
              className={`px-6 py-2 text-white rounded-lg bg-[var(--feel-primary)] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
            >
              {t('GENERAL.upload')}
            </button>
          </div>
        </>
      )}
    </form>
  );
};

export default UserImageForm;
